import { z } from "zod";
import {
  DELETE_ALL_LEADS_CONFIRM,
  isWebLink,
  LEAD_LIMITS,
  leadLabelSportSchema,
  leadRunSourceSchema,
  leadSourceSchema,
  leadSportSchema,
  leadStatusSchema,
  uaeEmirateSchema,
} from "../leads.ts";
import { isoDateSchema, isoDateTimeSchema, uuidSchema } from "./common.ts";

/**
 * The Lead Finder of Control Center: `/v1/admin/leads/*` for the screens, and the same shapes for
 * the connector (`/v1/mcp`). Staff only.
 */

const optionalText = (max: number) => z.string().trim().max(max).optional();
const nullableText = (max: number) => z.string().trim().max(max).nullable().optional();
/** A link a lead keeps and shows as an `href`: http or https only. */
const webLinkSchema = z.string().trim().max(LEAD_LIMITS.url).refine(isWebLink, "Use an http or https link.").optional();
const scoreSchema = z.number().int().min(LEAD_LIMITS.scoreMin).max(LEAD_LIMITS.scoreMax);

// --- labels ---------------------------------------------------------------------------------

export const adminLeadLabelSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** Null: the label adds nothing to the score; it is there to describe and filter. */
  score: z.number().int().nullable(),
  sport: leadLabelSportSchema,
  /** What Claude reads before a run. Empty: only a person applies it. */
  howToSpot: z.string(),
  /** Set on the one label Control Center applies itself ("Already on Teamna"). */
  systemKey: z.string().nullable(),
  /** The leads that carry it now. */
  leadCount: z.number().int(),
});
export type AdminLeadLabel = z.infer<typeof adminLeadLabelSchema>;

export const adminLeadLabelsSchema = z.object({ items: z.array(adminLeadLabelSchema) });

export const adminLeadLabelCreateSchema = z.object({
  name: z.string().trim().min(1).max(LEAD_LIMITS.labelName),
  score: scoreSchema.nullable().default(null),
  sport: leadLabelSportSchema.default("both"),
  howToSpot: z.string().trim().max(LEAD_LIMITS.howToSpot).default(""),
});
export type AdminLeadLabelCreate = z.input<typeof adminLeadLabelCreateSchema>;

export const adminLeadLabelUpdateSchema = z.object({
  name: z.string().trim().min(1).max(LEAD_LIMITS.labelName).optional(),
  score: scoreSchema.nullable().optional(),
  sport: leadLabelSportSchema.optional(),
  howToSpot: z.string().trim().max(LEAD_LIMITS.howToSpot).optional(),
});
export type AdminLeadLabelUpdate = z.infer<typeof adminLeadLabelUpdateSchema>;

// --- leads ----------------------------------------------------------------------------------

export const adminLeadAppliedLabelSchema = z.object({
  labelId: uuidSchema,
  name: z.string(),
  score: z.number().int().nullable(),
  /** `claude`, `system` (Control Center), or the email of the person who added it. */
  addedBy: z.string(),
  reason: z.string().nullable(),
  link: z.string().nullable(),
});
export type AdminLeadAppliedLabel = z.infer<typeof adminLeadAppliedLabelSchema>;

export const adminLeadPhoneSchema = z.object({ e164: z.string(), sourceUrl: z.string().nullable() });

export const adminLeadSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  sport: leadSportSchema,
  emirate: uaeEmirateSchema,
  area: z.string().nullable(),
  instagram: z.string().nullable(),
  facebook: z.string().nullable(),
  website: z.string().nullable(),
  mapsUrl: z.string().nullable(),
  phones: z.array(adminLeadPhoneSchema),
  source: leadSourceSchema,
  run: z.object({ id: uuidSchema, number: z.number().int() }).nullable(),
  status: leadStatusSchema,
  note: z.string().nullable(),
  followUpOn: isoDateSchema.nullable(),
  labels: z.array(adminLeadAppliedLabelSchema),
  /** Labels a person took off this lead. A run never puts them back. */
  removedLabelIds: z.array(uuidSchema),
  /** The sum of the label scores. Never stored. */
  score: z.number().int(),
  /** A person changed the lead (status, note, follow-up, labels). Delete run keeps it. */
  touched: z.boolean(),
  createdAt: isoDateTimeSchema,
});
export type AdminLead = z.infer<typeof adminLeadSchema>;

export const adminLeadsQuerySchema = z.object({
  runId: uuidSchema.optional(),
  sport: leadSportSchema.optional(),
  labelId: uuidSchema.optional(),
  status: leadStatusSchema.optional(),
  q: z.string().trim().max(120).optional(),
});
export type AdminLeadsQuery = z.infer<typeof adminLeadsQuerySchema>;

export const adminLeadsSchema = z.object({ items: z.array(adminLeadSchema) });

/** The facts of a place, as typed on the Leads page or sent by the connector. */
export const leadFactsSchema = z.object({
  name: z.string().trim().min(1).max(LEAD_LIMITS.name),
  sport: leadSportSchema,
  emirate: uaeEmirateSchema,
  area: optionalText(LEAD_LIMITS.name),
  instagram: optionalText(LEAD_LIMITS.url),
  facebook: optionalText(LEAD_LIMITS.url),
  website: optionalText(LEAD_LIMITS.url),
  mapsUrl: webLinkSchema,
  phones: z
    .array(z.object({ number: z.string().trim().min(5).max(30), sourceUrl: webLinkSchema }))
    .max(LEAD_LIMITS.phonesPerLead)
    .default([]),
  note: optionalText(LEAD_LIMITS.note),
});
export type LeadFacts = z.input<typeof leadFactsSchema>;

/** A lead typed on the Leads page. The labels are ids, added by the person. */
export const adminLeadCreateSchema = leadFactsSchema.extend({
  labelIds: z.array(uuidSchema).max(LEAD_LIMITS.labelsPerSave).default([]),
});
export type AdminLeadCreate = z.input<typeof adminLeadCreateSchema>;

export const adminLeadUpdateSchema = z.object({
  name: z.string().trim().min(1).max(LEAD_LIMITS.name).optional(),
  emirate: uaeEmirateSchema.optional(),
  area: nullableText(LEAD_LIMITS.name),
  status: leadStatusSchema.optional(),
  note: nullableText(LEAD_LIMITS.note),
  followUpOn: isoDateSchema.nullable().optional(),
});
export type AdminLeadUpdate = z.infer<typeof adminLeadUpdateSchema>;

export const adminLeadLabelChangeSchema = z.object({
  labelId: uuidSchema,
  /** `add` puts it on (and clears an earlier removal), `remove` takes it off for good for runs. */
  action: z.enum(["add", "remove"]),
});
export type AdminLeadLabelChange = z.infer<typeof adminLeadLabelChangeSchema>;

// --- runs -----------------------------------------------------------------------------------

export const adminLeadRunSchema = z.object({
  id: uuidSchema,
  number: z.number().int(),
  sport: leadSportSchema,
  emirate: uaeEmirateSchema,
  area: z.string().nullable(),
  source: leadRunSourceSchema,
  searches: z.array(z.string()),
  stopAt: z.number().int(),
  startedBy: z.string(),
  startedAt: isoDateTimeSchema,
  finishedAt: isoDateTimeSchema.nullable(),
  profilesRead: z.number().int().nullable(),
  skipped: z.number().int().nullable(),
  /** The leads this run saved that still exist. */
  leadCount: z.number().int(),
  report: z.string().nullable(),
  note: z.string().nullable(),
  deletedAt: isoDateTimeSchema.nullable(),
});
export type AdminLeadRun = z.infer<typeof adminLeadRunSchema>;

export const adminLeadRunsSchema = z.object({ items: z.array(adminLeadRunSchema) });

export const leadRunStartSchema = z.object({
  sport: leadSportSchema,
  emirate: uaeEmirateSchema,
  area: optionalText(LEAD_LIMITS.name),
  source: leadRunSourceSchema,
  searches: z.array(z.string().trim().min(1).max(120)).min(1).max(LEAD_LIMITS.runSearches),
  stopAt: z.number().int().min(1).max(LEAD_LIMITS.runMaxLeads).default(LEAD_LIMITS.runMaxLeads),
});
export type LeadRunStart = z.input<typeof leadRunStartSchema>;

export const adminLeadRunNoteSchema = z.object({ note: z.string().trim().max(LEAD_LIMITS.note).nullable() });

export const adminLeadRunDeletePreviewSchema = z.object({
  willDelete: z.number().int(),
  /** Leads of the run a person changed, or marked Do not contact. They stay. */
  kept: z.array(z.object({ id: uuidSchema, name: z.string() })),
});
export type AdminLeadRunDeletePreview = z.infer<typeof adminLeadRunDeletePreviewSchema>;

export const adminLeadRunDeleteResultSchema = z.object({ deleted: z.number().int(), kept: z.number().int() });

// --- label suggestions ----------------------------------------------------------------------

export const adminLeadLabelSuggestionSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  sport: leadLabelSportSchema,
  howToSpot: z.string(),
  score: z.number().int().nullable(),
  run: z.object({ id: uuidSchema, number: z.number().int() }).nullable(),
  /** The places Claude saw it on that still exist. */
  leads: z.array(z.object({ id: uuidSchema, name: z.string(), reason: z.string().nullable() })),
  suggestedBy: z.string(),
  createdAt: isoDateTimeSchema,
});
export type AdminLeadLabelSuggestion = z.infer<typeof adminLeadLabelSuggestionSchema>;

export const adminLeadLabelSuggestionsSchema = z.object({ items: z.array(adminLeadLabelSuggestionSchema) });

/** Add a suggestion as a label. The fields change it first; left out, the suggestion's own. */
export const adminLeadSuggestionAddSchema = adminLeadLabelUpdateSchema;

// --- start over -----------------------------------------------------------------------------

export const adminLeadsDeleteAllSchema = z.object({
  confirm: z.literal(DELETE_ALL_LEADS_CONFIRM),
  alsoLabels: z.boolean().default(false),
});
export type AdminLeadsDeleteAll = z.input<typeof adminLeadsDeleteAllSchema>;

export const adminLeadsDeleteAllResultSchema = z.object({
  deletedLeads: z.number().int(),
  keptDoNotContact: z.number().int(),
  deletedRuns: z.number().int(),
  deletedLabels: z.number().int(),
});
export type AdminLeadsDeleteAllResult = z.infer<typeof adminLeadsDeleteAllResultSchema>;

// --- the connector sign-ins -----------------------------------------------------------------

export const adminConnectorGrantSchema = z.object({
  id: uuidSchema,
  clientName: z.string(),
  staffEmail: z.string(),
  createdAt: isoDateTimeSchema,
  lastUsedAt: isoDateTimeSchema.nullable(),
});
export type AdminConnectorGrant = z.infer<typeof adminConnectorGrantSchema>;

export const adminConnectorSchema = z.object({
  /** The address to paste into Claude (Settings, Connectors). */
  url: z.string(),
  grants: z.array(adminConnectorGrantSchema),
});
export type AdminConnector = z.infer<typeof adminConnectorSchema>;

// --- the connector (MCP) ----------------------------------------------------------------------

/** A label Claude puts on a place: a name from the list, with what it saw and where. */
export const leadLabelEvidenceSchema = z.object({
  name: z.string().trim().min(1).max(LEAD_LIMITS.labelName),
  reason: optionalText(LEAD_LIMITS.reason),
  link: webLinkSchema,
});

/** Save a place from a run or a chat. A known place (same handle, page, website or number) is merged. */
export const leadSaveSchema = leadFactsSchema.extend({
  /** The run this place was found in. Left out for a chat. */
  runId: uuidSchema.optional(),
  labels: z.array(leadLabelEvidenceSchema).max(LEAD_LIMITS.labelsPerSave).default([]),
});
export type LeadSave = z.input<typeof leadSaveSchema>;

export const leadSaveResultSchema = z.object({
  created: z.boolean(),
  lead: adminLeadSchema,
  /** Labels not put on, and why (a person removed it from this lead, or it is on already). */
  skippedLabels: z.array(z.object({ name: z.string(), why: z.string() })),
});
export type LeadSaveResult = z.infer<typeof leadSaveResultSchema>;

/** Look a place up before opening it. Any one field is enough. */
export const leadFindSchema = z.object({
  instagram: optionalText(LEAD_LIMITS.url),
  facebook: optionalText(LEAD_LIMITS.url),
  website: optionalText(LEAD_LIMITS.url),
  phone: optionalText(30),
  name: optionalText(LEAD_LIMITS.name),
});
export type LeadFind = z.infer<typeof leadFindSchema>;

export const leadFindResultSchema = z.object({
  /** Leads with the same handle, page, website or number: the same place. */
  same: z.array(adminLeadSchema),
  /** Leads with a name like this one: maybe the same place, check. */
  similarNames: z.array(z.object({ id: uuidSchema, name: z.string(), emirate: z.string(), sport: leadSportSchema })),
});
export type LeadFindResult = z.infer<typeof leadFindResultSchema>;

export const leadRunFinishSchema = z.object({
  profilesRead: z.number().int().min(0).max(1000),
  skipped: z.number().int().min(0).max(1000),
  report: z.string().trim().max(LEAD_LIMITS.note).optional(),
});
export type LeadRunFinish = z.infer<typeof leadRunFinishSchema>;

export const leadSuggestLabelSchema = z.object({
  name: z.string().trim().min(1).max(LEAD_LIMITS.labelName),
  sport: leadLabelSportSchema.default("both"),
  howToSpot: z.string().trim().min(1).max(LEAD_LIMITS.howToSpot),
  score: scoreSchema.nullable().default(null),
  runId: uuidSchema.optional(),
  seenOn: z
    .array(z.object({ leadId: uuidSchema, reason: optionalText(LEAD_LIMITS.reason) }))
    .max(LEAD_LIMITS.runMaxLeads)
    .default([]),
});
export type LeadSuggestLabel = z.input<typeof leadSuggestLabelSchema>;
